import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import LanguageToggle from '../components/LanguageToggle';

interface ScoreRow {
  user_id: string;
  day: number;
  kpi: number | null;
  career_risk: number | null;
  procedural_risk: number | null;
  nickname: string | null;
  avatar_url: string | null;
  is_game_over: boolean;
  created_at: string;
}

const LeaderboardPage: React.FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<ScoreRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const load = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setCurrentUserId(session?.user.id ?? null);

      const [{ data: scores }, { data: profiles }] = await Promise.all([
        supabase
          .from('scores')
          .select('user_id, day, kpi, career_risk, procedural_risk, is_game_over, created_at')
          .order('day', { ascending: false })
          .order('kpi', { ascending: false, nullsFirst: false })
          .limit(200),
        supabase
          .from('profiles')
          .select('user_id, nickname, avatar_url'),
      ]);

      const profileMap: Record<string, { nickname: string; avatar_url: string | null }> = {};
      (profiles ?? []).forEach((p: { user_id: string; nickname: string; avatar_url: string | null }) => {
        profileMap[p.user_id] = { nickname: p.nickname, avatar_url: p.avatar_url };
      });

      // Deduplicate: Keep the best record for each user.
      const bestScores: Record<string, ScoreRow> = {};
      (scores ?? []).forEach((s: any) => {
        const existing = bestScores[s.user_id];
        // Logic: Higher Day is always better. If Day is same, higher KPI is better.
        const isBetter = !existing || 
          s.day > existing.day || 
          (s.day === existing.day && (s.kpi || 0) > (existing.kpi || 0));

        if (isBetter) {
          bestScores[s.user_id] = {
            ...s,
            nickname: profileMap[s.user_id]?.nickname ?? null,
            avatar_url: profileMap[s.user_id]?.avatar_url ?? null,
          };
        }
      });

      // Stable Sorting: Day > KPI > CreatedAt (tie-breaker)
      const merged = Object.values(bestScores).sort((a, b) => {
        if (b.day !== a.day) return b.day - a.day;
        if ((b.kpi || 0) !== (a.kpi || 0)) return (b.kpi || 0) - (a.kpi || 0);
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }).slice(0, 50);

      setRows(merged);
      setLoading(false);
    };

    load();
  }, []);

  const handleDeleteSelf = async () => {
    if (!currentUserId) return;
    if (!window.confirm(t('leaderboard.deleteConfirm'))) return;
    setDeleting(true);
    
    const { error } = await supabase.from('scores').delete().eq('user_id', currentUserId);
    
    if (error) {
      console.error('Delete error:', error);
      alert('Delete failed. Make sure migration 014 is applied.');
      setDeleting(false);
    } else {
      setRows(prev => prev.filter(r => r.user_id !== currentUserId));
      setDeleting(false);
    }
  };

  const hasSelfRow = rows.some(r => r.user_id === currentUserId);

  return (
    <div className="leaderboard-page">
      <div className="leaderboard-container">
        <div className="leaderboard-header">
          <h1>{t('leaderboard.title')}</h1>
          <div className="leaderboard-header-right">
            <LanguageToggle />
            <button className="leaderboard-back-btn" onClick={() => { window.location.search = ''; }}>
              {t('leaderboard.back')}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="leaderboard-loading">{t('loading')}</div>
        ) : rows.length === 0 ? (
          <div className="leaderboard-empty">{t('leaderboard.empty')}</div>
        ) : (
          <>
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th>{t('leaderboard.rank')}</th>
                  <th>{t('leaderboard.nickname')}</th>
                  <th>{t('leaderboard.day')}</th>
                  <th>KPI</th>
                  <th>{t('topbar.careerRisk')}</th>
                  <th>{t('topbar.proceduralRisk')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr
                    key={i}
                    className={`${row.user_id === currentUserId ? 'leaderboard-row-self' : ''} ${row.is_game_over ? 'leaderboard-row-dead' : ''}`}
                  >
                    <td className="leaderboard-rank">
                      {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
                    </td>
                    <td className="leaderboard-nickname">
                      {row.avatar_url ? (
                        <img
                          className="leaderboard-avatar"
                          src={row.avatar_url}
                          alt=""
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <span className="leaderboard-avatar-placeholder">
                          {(row.nickname ?? '?').charAt(0).toUpperCase()}
                        </span>
                      )}
                      {row.nickname ?? t('leaderboard.anonymous')}
                      {row.is_game_over && <span className="leaderboard-dead-tag" title="Game Over">💀</span>}
                    </td>
                    <td>{t('leaderboard.dayN', { day: row.day })}</td>
                    <td className="leaderboard-score">{row.kpi ?? '—'}</td>
                    <td className={row.career_risk != null && row.career_risk >= 70 ? 'leaderboard-risk-high' : ''}>
                      {row.career_risk ?? '—'}
                    </td>
                    <td className={row.procedural_risk != null && row.procedural_risk >= 70 ? 'leaderboard-risk-high' : ''}>
                      {row.procedural_risk ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {hasSelfRow && (
              <div className="leaderboard-delete-area">
                <button
                  className="leaderboard-delete-btn"
                  onClick={handleDeleteSelf}
                  disabled={deleting}
                >
                  {deleting ? t('leaderboard.deleting') : t('leaderboard.deleteMyScore')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LeaderboardPage;
